import { httpActions, facebookActions, iamThunk } from '../../actions';
import { logError } from '../../utils';


/**
 * facebookLogin - Top level API for logging in via facebook from a container
 * @desc
 * The Thunk first calls facebook login obtaining the user's permission to login with facebook
 * and fetching the required user data. If the promise resolves (all required FB user data is
 * obtained) we call the IAM facebook login thunk with FB data.
 */
export const facebookLogin = requestSettings => dispatch => {
	return dispatch(facebookConnect())
		.then(() => {
			return dispatch(iamThunk.iamFacebookLogin(requestSettings));
		});
};

/**
 * facebookConnect - Thunk that makes a request to the Facebook login API to initialise a facebook login
 * @desc
 * The Thunk first checks if the user is already connected via FB login. If not we make the
 * login connection attempt. A failure to connect triggers a rejection of the promise.
 * If already connected, or a successful connection is made, we contact Facebook for required
 * user-data which is then used to resolve the promise.
 * If the we don't have permission to access the users email, facebook will repromt.
 * @param {function} _logError - This is so that we can test the logger is fired
 * @returns {Promise} Redux thunk promise
*/
export const facebookConnect = (_logError = logError) => (dispatch, getState) => {
	const facebookLoginStatus = getState().FACEBOOK_loginStatus;
	if (!facebookLoginStatus) {
		return;
	}
	return new Promise((resolve, reject) => {
		const requestName = 'facebook_connect';
		dispatch(httpActions.facebookRequestMade(requestName));
		initFacebookLogin();

		// Log in in user and check permissions, we require email. FB pop up will re-prompt.
		function initFacebookLogin() {
			const fbOptions = {
				scope: getState().fbLoginScope || 'email',
				auth_type: 'rerequest',
			};
			FB.login(function({authResponse, status}) {
				if (status === 'not_authorized' || status === 'connected') {
					// not_authorized = connected to facebook and the app hasn't been authorised
					// connected = connected to facebook and the app has been authorised
					return getUserFacebookData(authResponse);
				} else {
					dispatch(httpActions.facebookRequestError(requestName, status));
					// returns unknown if the user closes the pop up, don't log this error
					if (status !== 'unknown') {
						_logError(new Error(`${requestName}: status: ${status}`));
					}
					return reject(status);
				}
			}, fbOptions);
		}

		// We are logged into Facebook - now get required user Facebook data
		function getUserFacebookData(authResponse) {
			if (authResponse) {
				FB.api('/me?fields=picture,email,first_name,last_name', function(response) {
					if (response.email) {
						dispatch(facebookActions.updateFacebookFirstName(response.first_name));
						dispatch(facebookActions.updateFacebookLastName(response.last_name));
						dispatch(facebookActions.updateFacebookEmail(response.email));
						dispatch(facebookActions.updateFacebookAccessToken(authResponse.accessToken));
						dispatch(httpActions.facebookRequestSuccess(requestName));
						return resolve();
					} else {
						dispatch(httpActions.facebookRequestError(requestName, 'fb_user_denied_email'));
						_logError(new Error('fb_user_denied_email'));
						return reject('fb_user_denied_email');
					}
				});
			} else {
				// The user has declined to authorise the JG Facebook app
				dispatch(httpActions.facebookRequestError(requestName, 'fb_user_denied_email'));
				_logError(new Error('fb_user_denied_app_authorisation'));
				return reject('fb_user_denied_app_authorisation');
			}
		}
	});
};

// Check if the user is logged in to Facebook
export const checkFacebookLoginStatus = cb => {
	FB && FB.getLoginStatus(response => cb(response));
};



// WEBPACK FOOTER //
// ../~/@jg/justgiving-app-login/app/actions/thunks/facebookThunk.js